import axios from 'axios';
import wsService from './websocket';

// Get environment variables with fallbacks
const API_BASE_URL = process.env.NODE_ENV === 'production' 
    ? 'https://scanner.exploitfinder.com/api'
    : 'http://localhost:3000/api';
const WS_URL = window.ENV?.wsUrl || 'ws://localhost:3000/ws';
const GOOGLE_CLIENT_ID = window.ENV?.googleClientId || '379964937752-n3ib70th3j0f308c6a5etbq894kogupk.apps.googleusercontent.com';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add request interceptor with logging
axiosInstance.interceptors.request.use((config) => {
    console.log('[API Request]', {
        url: config.url,
        method: config.method,
        data: config.data
    });

    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
});

// Add response interceptor with logging
axiosInstance.interceptors.response.use(
    (response) => {
        console.log('[API Response]', {
            url: response.config.url,
            status: response.status,
            data: response.data
        });
        return response;
    },
    (error) => {
        console.error('[API Response Error]', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        });
        
        // Gestione specifica errori di autenticazione
        if (error.response?.status === 401 || error.response?.status === 403) {
            localStorage.removeItem('token');
            window.dispatchEvent(new Event('authError'));
        }
        
        return Promise.reject(error);
    }
);

const debugLog = (message, data) => {
    console.log(`[API Debug] ${message}:`, data);
};

// Add this helper function at the top
function formatDateForApi(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error('Invalid date');
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

// Add this helper function near the top with other helpers
const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
};

// Add helper function to get token
const getToken = () => localStorage.getItem('token');

const scanApi = {
    async startScan(data) {
        try {
            // Verifica autenticazione prima di procedere
            const authStatus = await this.checkAuthStatus();
            if (!authStatus.isAuthenticated) {
                throw new Error('AUTH_REQUIRED');
            }

            // Aggiorna la quota prima di procedere
            await this.refreshQuota();

            let payload = typeof data === 'string' ? { targetUrl: data } : data;
            
            const response = await axiosInstance.post('/scans', payload);
            
            // Aggiorna la quota dopo la scansione
            if (response.data.quotaInfo) {
                this.notifyQuotaUpdate(response.data.quotaInfo);
            }
            
            return response.data;
        } catch (error) {
            if (error.message === 'AUTH_REQUIRED') {
                window.dispatchEvent(new Event('authError'));
                throw error;
            }
            if (error.response?.status === 403) {
                if (error.response.data?.error === 'Scan quota exceeded') {
                    this.notifyQuotaUpdate(error.response.data.quotaInfo);
                    throw new Error('QUOTA_EXCEEDED');
                }
            }
            throw error;
        }
    },

    async createTarget(url) {
        try {
            const response = await axiosInstance.post('/targets', {
                address: url,
                description: `Target for ${url}`,
                criticality: 10
            });
            return response.data?.target_id;
        } catch (error) {
            console.error('Failed to create target:', error);
            throw error;
        }
    },

    async scheduleNewScan(targetId, scheduledTime) {
        const response = await axiosInstance.post('/scans', {
            target_id: targetId,
            profile_id: "11111111-1111-1111-1111-111111111111",
            schedule: {
                disable: false,
                start_date: scheduledTime,
                time_sensitive: false
            }
        });
        return response.data;
    },

    notifyQuotaUpdate(quotaInfo) {
        const event = new CustomEvent('quotaUpdate', { detail: quotaInfo });
        window.dispatchEvent(event);
    },

    notifyAuthUpdate() {
        window.dispatchEvent(new Event('authUpdate'));
    },

    async login(credentials) {
        try {
            const response = await axiosInstance.post('/login', credentials);
            
            if (response.data?.error === 'Account not activated') {
                throw new Error('ACCOUNT_PENDING_APPROVAL');
            }
            
            if (response.data?.token) {
                localStorage.setItem('token', response.data.token);
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                
                if (response.data.scan_quota) {
                    this.notifyQuotaUpdate({
                        total: response.data.scan_quota,
                        used: response.data.scans_used,
                        remaining: response.data.scan_quota - response.data.scans_used
                    });
                }
                
                this.notifyAuthUpdate();
                // Attendere un momento prima del refresh
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            }
            return response.data;
        } catch (error) {
            if (error.response?.status === 403 && error.response.data?.requiresApproval) {
                throw new Error('ACCOUNT_PENDING_APPROVAL');
            }
            throw error;
        }
    },

    logout() {
        localStorage.removeItem('token');
        delete axiosInstance.defaults.headers.common['Authorization'];
        this.notifyAuthUpdate();
        // Aggiungi refresh dopo logout
        window.location.reload();
    },

    async stopScan(scanId) {
        const response = await axiosInstance.post(`/scans/${scanId}/stop`);
        return response.data;
    },

    async getScan(scanId) {
        try {
            const [scanStats, vulns] = await Promise.all([
                this.getStatistics(scanId),
                this.getVulnerabilities(scanId)
            ]);
            return {
                scanId,
                stats: scanStats,
                vulnerabilities: vulns,
                targetUrl: scanStats.targetUrl,
                status: scanStats.status
            };
        } catch (error) {
            console.error('Failed to get scan:', error);
            throw error;
        }
    },

    async getVulnerabilities(scanId) {
        try {
            const response = await axiosInstance.get(`/scans/${scanId}/vulnerabilities`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch vulnerabilities:', error);
            throw error;
        }
    },

    async getStatistics(scanId) {
        try {
            const response = await axiosInstance.get(`/scans/${scanId}/statistics`);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch scan statistics:', error);
            throw error;
        }
    },

    async resumeScan(scanId) {
        const response = await axiosInstance.get(`/scans/${scanId}`);
        return response.data;
    },

    async getReportTemplates() {
        const response = await axiosInstance.get('/reports/templates');
        return response.data;
    },

    async generateReport(templateId, scanId) {
        try {
            debugLog('Starting report generation', { templateId, scanId });
            
            // Initial report creation
            const response = await axiosInstance.post('/reports', {
                templateId,
                scanId
            }, {
                timeout: 60000 // Increase initial timeout to 60 seconds
            });

            if (!response.data?.report_id) {
                throw new Error('No report ID received from server');
            }

            const reportId = response.data.report_id;
            let status = null;
            let attempts = 0;
            const maxAttempts = 60; // 5 minutes total (5s intervals)
            const pollingInterval = 5000; // 5 seconds

            debugLog('Starting report status polling', { reportId });

            // Poll for report completion
            while (attempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, pollingInterval));
                
                try {
                    const statusResponse = await this.checkReportStatus(reportId);
                    status = statusResponse?.status;
                    
                    debugLog('Report status check', { reportId, status, attempt: attempts + 1 });

                    if (status === 'completed') {
                        debugLog('Report generation completed', { reportId });
                        return {
                            reportId,
                            status: 'completed'
                        };
                    } else if (status === 'failed' || status === 'aborted') {
                        throw new Error(`Report generation ${status}`);
                    }
                } catch (pollError) {
                    debugLog('Poll attempt failed', { attempt: attempts + 1, error: pollError.message });
                    // Continue polling even if one attempt fails
                }
                
                attempts++;
            }

            throw new Error('Report generation timed out after 5 minutes');

        } catch (error) {
            debugLog('Report generation error', { error: error.message });
            throw new Error(error.response?.data?.details || error.message);
        }
    },

    async downloadReport(reportId) {
        try {
            debugLog('Starting report download', { reportId });
            
            // Get report status first
            const status = await this.checkReportStatus(reportId);
            
            if (!status || status.status !== 'completed') {
                throw new Error('Report not ready for download');
            }

            // Direct download using the saved download URL
            const response = await axiosInstance.get(`/reports/${reportId}/download`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'text/html',
                    'Cache-Control': 'no-cache'
                }
            });

            // Create and trigger download
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: 'text/html' })
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report-${reportId}.html`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            debugLog('Report download completed', { reportId });
            return true;

        } catch (error) {
            debugLog('Error downloading report', { error, reportId });
            throw error;
        }
    },

    async checkReportStatus(reportId) {
        try {
            const response = await axiosInstance.get(`/reports/${reportId}/status`);
            // Aggiungi log per debugging
            debugLog('Report status response', response.data);
            return response.data;
        } catch (error) {
            debugLog('Error checking report status', { reportId, error });
            throw error;
        }
    },

    async getReports() {
        try {
            const response = await axiosInstance.get('/reports');
            return response.data;
        } catch (error) {
            console.error('Error fetching reports:', error);
            throw error;
        }
    },

    async getScanHistory() {
        try {
            const response = await axiosInstance.get('/scans');
            const scans = response.data.map(scan => {
                return {
                    ...scan,
                    status: scan.status,
                    scheduled_time: scan.scheduled_time || null
                };
            });
            return scans;
        } catch (error) {
            console.error('Failed to fetch scan history:', error);
            throw error;
        }
    },

    async getScheduledScans() {
        try {
            const response = await axiosInstance.get('/schedules');
            return response.data;
        } catch (error) {
            console.error('Failed to fetch scheduled scans:', error);
            throw error;
        }
    },

    subscribeToUpdates(scanId, callbacks) {
        const ws = new WebSocket(`${WS_URL}?scanId=${scanId}`);
        
        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'scan_update' && callbacks.onUpdate) {
                callbacks.onUpdate(data.data);
            } else if (data.type === 'scan_complete' && callbacks.onComplete) {
                callbacks.onComplete(data.data);
            } else if (data.type === 'scan_error' && callbacks.onError) {
                callbacks.onError(data.error);
            }
        };

        ws.onerror = (error) => {
            if (callbacks.onError) {
                callbacks.onError(error);
            }
        };

        return () => ws.close();
    },

    async getCurrentUser() {
        try {
            console.log('[API] Getting current user profile...');
            const response = await axiosInstance.get('/user/profile');
            console.log('[API] User profile response:', response.data);
            return response.data;
        } catch (error) {
            console.error('[API] Error fetching user profile:', error.response || error);
            throw error;
        }
    },

    async getScanQuota() {
        try {
            const response = await this.getCurrentUser();
            return response.quotaInfo;
        } catch (error) {
            console.error('[API] Error fetching scan quota:', error);
            throw error;
        }
    },

    async updateProfile(data) {
        try {
            const response = await axiosInstance.put('/user/profile', data);
            return response.data;
        } catch (error) {
            console.error('Failed to update profile:', error);
            throw error;
        }
    },

    async getReportPreview(reportId) {
        try {
            // Prima verifica lo stato
            const status = await this.checkReportStatus(reportId);
            
            if (status.status !== 'completed') {
                throw new Error('Report not ready');
            }

            // Poi ottieni il preview
            const response = await axiosInstance.get(`/reports/${reportId}/preview`, {
                headers: {
                    ...getAuthHeader(),
                    'Accept': 'text/html'
                },
                responseType: 'text'
            });

            return response.data;
        } catch (error) {
            console.error('Error getting report preview:', error);
            throw error;
        }
    },

    async convertReportToPdf(reportId) {
        throw new Error('PDF conversion is handled by browser print');
    },

    async deleteScan(scanId) {
        try {
            debugLog('Deleting scan', { scanId });
            
            if (!scanId) {
                throw new Error('Scan ID is required');
            }

            const response = await axiosInstance.delete(`/scans/${scanId}`, {
                headers: getAuthHeader()
            });

            if (response.status !== 200) {
                throw new Error('Failed to delete scan');
            }

            return response.data;
        } catch (error) {
            console.error('Failed to delete scan:', error);
            throw error;
        }
    },

    async checkAuthStatus() {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return { isAuthenticated: false, error: 'No token found' };
            }

            // Verifica il token con una richiesta al server
            const response = await axiosInstance.get('/user/profile');
            return { 
                isAuthenticated: true, 
                user: response.data,
                token 
            };
        } catch (error) {
            // Se c'è un errore 401/403, il token non è più valido
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                delete axiosInstance.defaults.headers.common['Authorization'];
            }
            return { 
                isAuthenticated: false, 
                error: error.response?.data?.error || error.message 
            };
        }
    },

    async refreshQuota() {
        try {
            const response = await axiosInstance.get('/user/profile');
            if (response.data?.quotaInfo) {
                this.notifyQuotaUpdate(response.data.quotaInfo);
            }
            return response.data.quotaInfo;
        } catch (error) {
            console.error('Failed to refresh quota:', error);
            throw error;
        }
    },

    async createPaymentIntent(data) {
        try {
            const response = await axiosInstance.post('/payments/create-intent', data);
            return response.data;
        } catch (error) {
            console.error('Failed to create payment intent:', error);
            throw error;
        }
    },

    async confirmPayment(paymentIntentId) {
        try {
            const response = await axiosInstance.post('/payments/confirm', {
                paymentIntentId
            });
            
            // Reindirizza alla pagina di successo dopo il pagamento
            window.location.href = '/payment/success';
            
            return response.data;
        } catch (error) {
            console.error('Failed to confirm payment:', error);
            throw error;
        }
    },

    async updatePassword(data) {
        try {
            const response = await axiosInstance.post(`${API_BASE_URL}/user/update-password`, data, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    async register(userData) {
        try {
            const response = await axiosInstance.post('/register', userData);
            
            if (response.data?.token) {
                localStorage.setItem('token', response.data.token);
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                
                if (response.data.scan_quota) {
                    this.notifyQuotaUpdate({
                        total: response.data.scan_quota,
                        used: response.data.scans_used,
                        remaining: response.data.scan_quota - response.data.scans_used
                    });
                }
                
                this.notifyAuthUpdate();
            }
            return response.data;
        } catch (error) {
            console.error('Registration failed:', error);
            throw error;
        }
    },

    // Metodi di autenticazione
    async loginWithGoogle(googleToken) {
        try {
            debugLog('Tentativo di login con Google', {
                tokenLength: googleToken?.length
            });

            // Decodifica il token JWT per ottenere le informazioni dell'utente
            const tokenParts = googleToken.split('.');
            const payload = JSON.parse(atob(tokenParts[1]));

            debugLog('Token payload', {
                email: payload.email,
                name: payload.name,
                picture: payload.picture
            });

            // Verifica che l'email sia verificata
            if (!payload.email_verified) {
                throw new Error('È richiesta un\'email verificata');
            }

            const response = await axiosInstance.post('/auth/google', { 
                token: googleToken,
                email: payload.email,
                name: payload.name,
                given_name: payload.given_name,
                family_name: payload.family_name,
                picture: payload.picture,
                email_verified: payload.email_verified
            });
            
            debugLog('Risposta login Google', {
                status: response.status,
                hasToken: !!response.data?.token,
                data: response.data
            });
            
            if (response.data?.token) {
                localStorage.setItem('token', response.data.token);
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                
                if (response.data.scan_quota) {
                    this.notifyQuotaUpdate({
                        total: response.data.scan_quota,
                        used: response.data.scans_used,
                        remaining: response.data.scan_quota - response.data.scans_used
                    });
                }
                
                this.notifyAuthUpdate();
            }
            
            return response.data;
        } catch (error) {
            debugLog('Errore durante il login con Google', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status
            });

            if (error.response?.status === 403 && error.response.data?.requiresApproval) {
                throw new Error('ACCOUNT_PENDING_APPROVAL');
            }

            if (error.response?.status === 400) {
                throw new Error(error.response.data?.error || 'Errore di validazione del token Google');
            }

            throw error;
        }
    },

    async requestEmailPin(email) {
        try {
            debugLog('Requesting PIN for email', { email });
            const response = await axiosInstance.post('/auth/email/request-pin', { email });
            return response.data;
        } catch (error) {
            console.error('Errore durante la richiesta del PIN:', error);
            throw error;
        }
    },

    async verifyEmailPin(email, pin) {
        try {
            debugLog('Verifying PIN', { email, pin });
            const response = await axiosInstance.post('/auth/email/verify-pin', { 
                email, 
                pin 
            });
            
            if (response.data?.token) {
                localStorage.setItem('token', response.data.token);
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                this.notifyAuthUpdate();
            }
            return response.data;
        } catch (error) {
            console.error('Errore durante la verifica del PIN:', error);
            throw error;
        }
    },

    async startSubdomainScan(domain, options = { nmap: false }) {
        try {
            const response = await axiosInstance.post('/subdomain-scan', { 
                domain,
                enableNmap: options.nmap
            });
            return response.data;
        } catch (error) {
            console.error('Failed to start subdomain scan:', error);
            throw error;
        }
    },

    async getSubdomainScanStatus(scanId) {
        const response = await axiosInstance.get(`/subdomain-scan/${scanId}`);
        return response.data;
    },

    async resumeSubdomainScan(scanId) {
        const response = await axiosInstance.get(`/subdomain-scan/${scanId}/resume`);
        return response.data;
    },

    async stopSubdomainScan(scanId) {
        const response = await axiosInstance.post(`/subdomain-scan/${scanId}/stop`);
        return response.data;
    },

    subscribeToSubdomainUpdates(scanId, callbacks) {
        const ws = new WebSocket(`${WS_URL}/api/subdomain?scanId=${scanId}`);
        
        ws.onopen = () => {
            console.log('WebSocket connection opened for scan:', scanId);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('WebSocket message received:', data);
                
                if (data.scanId === scanId) {
                    switch (data.type) {
                        case 'scan_resumed':
                            callbacks.onResumed?.(data.data);
                            break;
                        case 'progress':
                            callbacks.onProgress?.(data.data);
                            break;
                        case 'subdomains_found':
                            callbacks.onSubdomains?.(data.data);
                            break;
                        case 'scan_complete':
                            callbacks.onComplete?.(data.data);
                            localStorage.removeItem(`scan_${data.data.domain}`);
                            ws.close();
                            break;
                        case 'scan_error':
                            callbacks.onError?.(data.error);
                            ws.close();
                            break;
                        case 'scan_stopped':
                            callbacks.onComplete?.({ ...data.data, stopped: true });
                            ws.close();
                            break;
                    }
                }
            } catch (error) {
                console.error('Error processing WebSocket message:', error);
                callbacks.onError?.('WebSocket message processing error');
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            callbacks.onError?.('WebSocket connection error');
        };

        ws.onclose = () => {
            console.log('WebSocket connection closed for scan:', scanId);
        };

        return () => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
        };
    }
};

const logout = async () => {
    try {
        // Chiama l'endpoint di logout se esiste
        await axios.post('/api/logout');
    } catch (error) {
        console.error('Logout error:', error);
    } finally {
        // Rimuovi il token e altri dati di autenticazione
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
    }
};

export async function startSubFinderScan() {
  const { data } = await axios.post('/api/subfinder360/start');
  return data;
}

export async function getSubFinderResults(scanId) {
  const { data } = await axios.get(`/api/subfinder360/results/${scanId}`);
  return data;
}

export { WS_URL };
export default scanApi;
